(() => {
  if (customElements.get('loovi-refill-offer')) return;
  customElements.define('loovi-refill-offer', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const controller = this.controller;
      const initialize = () => { if (this.controller === controller) this.initialize(); };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true, signal: this.controller.signal });
      } else queueMicrotask(initialize);
    }

    initialize() {
      if (!this.isConnected) return;
      let data;
      try { data = JSON.parse(this.querySelector('[data-refill-data]').textContent); }
      catch { return; } // Leave the real, server-rendered one-time purchase form available.
      if (!Array.isArray(data.variants) || !data.variants.length) return;
      this.variants = data.variants;
      this.form = this.querySelector('[data-refill-form]');
      this.variantSelect = this.querySelector('[data-refill-variant]');
      this.scent = this.querySelector('[data-refill-scent]');
      this.amounts = [...this.querySelectorAll('[data-refill-amount]')];
      this.modes = [...this.querySelectorAll('[data-refill-mode]')];
      this.planSelect = this.querySelector('[data-refill-plan-select]');
      this.planInput = this.querySelector('[data-refill-plan-input]');
      this.button = this.querySelector('[data-refill-submit]');
      this.status = this.querySelector('[data-refill-status]');
      this.fields = this.querySelector('[data-refill-fields]');
      if (!this.form || !this.variantSelect || !this.scent || !this.amounts.length || !this.button) return;
      if (!this.initialized) {
        const mainScent = this.closest('.shopify-section')?.querySelector('[data-loovi-fragrance]')?.value || this.dataset.mainScent;
        const first = this.variants.find(variant => String(variant.id) === this.dataset.initialVariant)
          || this.variants.find(variant => variant.available && variant.options[0] === mainScent)
          || this.variants.find(variant => variant.available) || this.variants[0];
        this.scent.value = first.options[0];
        this.amounts.forEach(input => { input.checked = input.value === first.options[1]; });
        this.modes[0].checked = true;
      }
      this.initialized = true;
      this.querySelector('.loovi-refill__fallback').hidden = true;
      this.querySelector('.loovi-refill__enhanced').hidden = false;
      this.querySelector('[data-refill-price-wrap]').hidden = false;
      this.form.addEventListener('change', () => this.update(), { signal: this.controller.signal });
      this.form.addEventListener('submit', event => this.submit(event), { signal: this.controller.signal });
      this.busy = false;
      this.fields.disabled = false;
      this.update();
    }

    update() {
      const amount = this.amounts.find(input => input.checked)?.value;
      this.variant = this.variants.find(variant => variant.options[0] === this.scent.value && variant.options[1] === amount);
      const plans = this.variant?.plans || [];
      const previousPlan = this.planSelect.value;
      this.planSelect.replaceChildren(...plans.map(plan => {
        const option = document.createElement('option');
        option.value = String(plan.id);
        option.textContent = `${plan.name} · ${plan.priceLabel} pro Lieferung`;
        return option;
      }));
      if (plans.some(plan => String(plan.id) === previousPlan)) this.planSelect.value = previousPlan;
      this.querySelector('[data-refill-modes]').hidden = !plans.length;
      if (!plans.length) this.modes[0].checked = true;
      this.subscription = this.modes.find(input => input.checked)?.value === 'subscription' && plans.length > 0;
      this.plan = this.subscription ? plans.find(plan => String(plan.id) === this.planSelect.value) : null;
      this.querySelector('[data-refill-plan-choice]').hidden = !this.subscription || plans.length < 2;
      this.querySelector('[data-refill-twint]').hidden = !plans.length;
      const terms = this.querySelector('[data-refill-terms]');
      terms.hidden = !this.subscription;
      terms.textContent = this.plan ? `${this.plan.name}: ${this.plan.priceLabel} pro Lieferung. Monatliche Lieferung und automatische Zahlung. Jederzeit kündbar.` : '';
      this.planInput.disabled = !this.plan;
      this.planInput.value = this.plan ? String(this.plan.id) : '';
      this.variantSelect.value = this.variant ? String(this.variant.id) : '';
      this.querySelector('[data-refill-price]').textContent = (this.plan || this.variant)?.priceLabel || 'Nicht verfügbar';
      this.querySelector('[data-refill-price-note]').textContent = this.subscription ? 'pro Lieferung · monatlich' : 'einmalig · pro Packung';
      this.button.disabled = this.busy || !this.variant?.available;
      if (!this.busy) {
        this.button.innerHTML = this.variant?.available ? 'Refills hinzufügen <span aria-hidden="true">↗</span>' : 'Aktuell ausverkauft';
        this.status.textContent = this.variant?.available ? '' : 'Diese Refills sind momentan nicht verfügbar.';
        this.removeAttribute('data-error');
        this.querySelector('[data-refill-cart-link]').hidden = true;
      }
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.variant?.available) return;
      this.busy = true;
      this.fields.disabled = true;
      this.button.disabled = true;
      this.button.textContent = 'Wird hinzugefügt …';
      this.button.setAttribute('aria-busy', 'true');
      this.removeAttribute('data-error');
      this.status.textContent = '';
      this.querySelector('[data-refill-cart-link]').hidden = true;
      const item = { id: this.variant.id, quantity: 1 };
      if (this.plan) item.selling_plan = this.plan.id;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { items: [item], sections_url: window.location.pathname };
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = window.routes?.cart_url || `${root}cart`;
      const addUrl = window.routes?.cart_add_url || `${root}cart/add.js`;
      const lifecycle = this.controller;
      const requestController = new AbortController();
      this.requestController = requestController;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; requestController.abort(); }, 15000);
      let receivedResponse = false;
      let added = false;
      try {
        if (typeof cart?.getSectionsToRender === 'function') payload.sections = cart.getSectionsToRender().map(section => section.id);
        cart?.setActiveElement?.(this.button);
        const response = await fetch(addUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload), signal: requestController.signal
        });
        const result = await response.json();
        receivedResponse = true;
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Die Refills konnten nicht hinzugefügt werden. Bitte versuche es erneut.');
        added = true;
        if (!this.isConnected || lifecycle !== this.controller) return;
        if (typeof cart?.renderContents === 'function' && result.sections && Object.values(result.sections).every(value => typeof value === 'string')) {
          try {
            cart.classList.remove('is-empty');
            cart.renderContents(result);
          } catch { window.location.assign(cartUrl); }
        } else window.location.assign(cartUrl);
      } catch (error) {
        if (!this.isConnected || lifecycle !== this.controller || (error.name === 'AbortError' && !timedOut)) return;
        this.setAttribute('data-error', '');
        this.status.textContent = receivedResponse ? error.message : 'Die Bestätigung fehlt. Prüfe bitte deinen Warenkorb, bevor du erneut hinzufügst.';
        this.querySelector('[data-refill-cart-link]').hidden = receivedResponse;
      } finally {
        clearTimeout(timeout);
        if (this.isConnected && lifecycle === this.controller) {
          this.busy = false;
          this.fields.disabled = false;
          this.button.disabled = !this.variant?.available;
          this.button.removeAttribute('aria-busy');
          this.button.innerHTML = 'Refills hinzufügen <span aria-hidden="true">↗</span>';
          if (added) this.status.textContent = 'Deine Refills wurden hinzugefügt.';
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
    }
  });
})();
